import { XIcon } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useRef } from "react";
import { cn } from "../../lib/cn";
import { Button } from "./button";

export function Dialog({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  width = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  eyebrow?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: "md" | "lg";
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      className={cn(
        "rounded-card border-line bg-surface text-body m-auto border p-0 shadow-[0_32px_80px_-24px_rgb(0_0_0/0.75)]",
        width === "md" ? "w-[min(480px,calc(100vw-2rem))]" : "w-[min(820px,calc(100vw-2rem))]",
      )}
    >
      {open && (
        <div className="flex max-h-[85vh] flex-col">
          <header className="border-line flex items-start justify-between gap-3 border-b px-5 py-3.5">
            <div>
              {eyebrow && (
                <p className="text-micro text-faint mb-0.5 font-semibold uppercase">{eyebrow}</p>
              )}
              <h2 className="display text-heading text-ink">{title}</h2>
            </div>
            <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close">
              <XIcon />
            </Button>
          </header>
          <div className="overflow-y-auto p-5">{children}</div>
          {footer && (
            <footer className="border-line bg-well/60 flex justify-end gap-2 border-t px-5 py-3">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  danger,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} disabled={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-ui-lg text-body leading-relaxed">{message}</p>
    </Dialog>
  );
}
